import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTabGroup } from '@angular/material/tabs';
import { Namespace } from 'enums-lib';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { UtilsService } from 'utils-service-lib';
import { DialogConfirmComponent, IDialogConfirm } from '../../common-components/dialog-confirm/dialog-confirm.component';
import { Adv2ConfigService } from '../../services/adv2-config.service';
import { EntityTypeService } from '../../services/entity-type/entity-type.service';
import { SalesforceMappingStoreService } from '../../services/salesforce/salesforce-mapping-store.service';
import { Adv2Config, ISalesforceConfig } from './adv2-config';
import { controlsToEmptyFields, emptyFieldsToControls } from './field-options/empty-fields.util';
import { controlsToDecimalFormat, controlsToIntegerFormat, numberFormatToControls } from './field-options/number-format.util';
import { NoSignatureProviderComponent } from './signature/none/no-signature-provider.component';
import { SignatureTabComponent } from './signature/signature-tab.component';

@Component({
    selector: 'app-adv2-config',
    templateUrl: './adv2-config.component.html',
    styleUrls: ['./adv2-config.component.scss'],
    standalone: false
})
export class Adv2ConfigComponent implements OnInit, AfterViewInit, OnDestroy {
  form: UntypedFormGroup;
  formReady = false;
  emptyFieldsFormArray: UntypedFormArray;
  validationIcon: string;
  isNewClient: boolean;
  salesforceConfig: ISalesforceConfig = { objects: [] };
  isSalesforceNamespace = false;
  hasValidSalesforceMappings = true;

  @ViewChild(MatTabGroup) tabGroup: MatTabGroup;
  @ViewChild(SignatureTabComponent) signatureTab: SignatureTabComponent;

  private viewReady = false;
  private pendingSignature: { providerName: string; signature: unknown } | null = null;
  private signatureGroupSubscription: Subscription;
  private validationIconTimeout: ReturnType<typeof setTimeout>;

  private static DEFAULT_DATE_FORMAT = 'yyyy';
  private static DEFAULT_OUTPUT_FORMAT = 'docx';

  constructor(
    private formBuilder: UntypedFormBuilder,
    private dialogRef: MatDialogRef<any>,
    private matDialog: MatDialog,
    private adv2ConfigService: Adv2ConfigService,
    private entityTypeService: EntityTypeService,
    private salesforceMappingStore: SalesforceMappingStoreService,
    private changeDetectorRef: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA)
    public data: { namespace: string; focusTab: number }
  ) {
    this.emptyFieldsFormArray = new UntypedFormArray([]);
    this.isSalesforceNamespace = (this.data?.namespace ?? '').toLowerCase() === Namespace.salesforce;
    this.hasValidSalesforceMappings = !this.isSalesforceNamespace;
  }

  get fieldOptionsGroup(): UntypedFormGroup {
    return this.form.get('fieldOptions') as UntypedFormGroup;
  }

  get signatureGroup(): UntypedFormGroup {
    return this.form.get('signature') as UntypedFormGroup;
  }

  get documentOptionsGroup(): UntypedFormGroup {
    return this.form.get('documentOptions') as UntypedFormGroup;
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      fieldOptions: this.formBuilder.group({
        dateFormat: null,
        numberDecimalPlaces: null,
        numberDecimalSeparator: null,
        numberThousandsSeparator: null,
        emptyFields: this.emptyFieldsFormArray,
      }),
      signature: this.formBuilder.group({
        signatureProvider: new UntypedFormControl(NoSignatureProviderComponent.NAME),
        errorNotificationEmail: new UntypedFormControl('', (control) =>
          control.value ? UtilsService.emailValidator()(control) : null,
        ),
        credentials: new UntypedFormGroup({}),
      }),
      documentOptions: this.formBuilder.group({
        outputFormat: null,
      }),
    });

    // the signature group itself is never replaced, only its `credentials` control,
    // so a single subscription covers every provider swap
    this.signatureGroupSubscription = this.signatureGroup.valueChanges
      .pipe(debounceTime(250))
      .subscribe((_) => this.updateValidationIcon());

    // TODO: replace with the actual config returned by the server
    this.adv2ConfigService.getConfig().subscribe({
      next: (exchange: Adv2Config) => {
        this.initialiseForm(exchange.config, exchange.signature);
      },
      error: (err) => {
        if (err.status !== 404) {
          throw err;
        }

        this.isNewClient = true;
        this.initialiseForm();
      },
      complete: () => {
        this.focusTab();
      },
    });
  }

  ngAfterViewInit(): void {
    this.viewReady = true;

    // config can land before the view exists (a synchronous or cached response), in
    // which case the signature tab is seeded here, once its own view query has run.
    if (this.pendingSignature) {
      this.applyPendingSignature();
      // seeding changes signatureGroup.invalid, which the tab label has already been
      // checked against in this pass
      this.changeDetectorRef.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.signatureGroupSubscription?.unsubscribe();
    clearTimeout(this.validationIconTimeout);
  }

  focusTab(): void {
    setTimeout(() => {
      if (this.data && this.data.focusTab !== undefined) {
        this.tabGroup.selectedIndex = this.data.focusTab;
      }
    }, 1000); // Give the animation time to complete
  }

  onSubmit(): void {
    const dsSig = this.noSignatureProviderSelected() ? null : this.signatureGroup.get('credentials').value;
    const config: { config: any; signature: any } = {
      config: undefined,
      signature: undefined,
    };
    config.config = {
      dateFormat: this.fieldOptionsGroup.get('dateFormat').value,
      signatureId: dsSig?.id,
      signatureProviderName: dsSig?.providerName,
      emptyFields: controlsToEmptyFields(this.emptyFieldsFormArray),
      outputFormat: this.documentOptionsGroup.get('outputFormat').value,
      salesforce: this.salesforceConfig,
      errorNotificationEmail: this.signatureGroup.get('errorNotificationEmail').value?.trim() || null,
      integerFormat: controlsToIntegerFormat(this.fieldOptionsGroup),
      decimalFormat: controlsToDecimalFormat(this.fieldOptionsGroup),
    };
    config.signature = dsSig;
    this.adv2ConfigService.setConfig(config);
    this.salesforceMappingStore.primeFromConfig(this.salesforceConfig);
    this.entityTypeService.clear();
    this.dialogRef.close();
  }

  discardLocalChanges(e = null): void {
    if (e) {
      e.preventDefault();
    } // prevent submission of parent form.

    if (this.form.dirty) {
      const confirmDialogRef: MatDialogRef<DialogConfirmComponent> = this.matDialog.open(DialogConfirmComponent, {
        width: '45vw',
        height: 'auto',
        autoFocus: true,
        panelClass: this.data.namespace + '-namespace',
        data: {
          title: `Changes made`,
          titleIcon: 'info_outline',
          message: this.allowSave ? 'Would you like to save your changes? ' : 'You have unsaved changes ',
          positiveText: 'Discard',
          negativeText: 'Go Back',
          alternativeText: this.allowSave ? 'Save' : null,
        } as IDialogConfirm,
      });

      confirmDialogRef.afterClosed().subscribe((takeAction: any) => {
        if (!takeAction) {
          return;
        } else {
          this.dialogRef.close(false);
        }

        if (takeAction === 'Save') {
          this.onSubmit();
        }
      });
    } else {
      this.dialogRef.close(false);
    }
  }

  onSalesforceConfigChange(config: ISalesforceConfig): void {
    this.salesforceConfig = config;
    this.form.markAsDirty();
  }

  onSalesforceMappingsValidity(isValid: boolean): void {
    this.hasValidSalesforceMappings = this.isSalesforceNamespace ? isValid : true;
  }

  get allowSave(): boolean {
    return this.form.valid && this.form.dirty && this.form.errors === null;
  }

  private updateValidationIcon(): void {
    clearTimeout(this.validationIconTimeout);
    this.validationIconTimeout = setTimeout((_) => {
      if (this.signatureGroup.valid) {
        this.validationIcon = 'check_circle';
      } else {
        this.validationIcon = 'error';
      }
    }, 300);
  }

  // TODO: type this as best you can!
  private initialiseForm(config?: any, signature?: any): void {
    this.fieldOptionsGroup.get('dateFormat').setValue(config?.dateFormat ?? Adv2ConfigComponent.DEFAULT_DATE_FORMAT);

    const numberFormat = numberFormatToControls(config?.decimalFormat);
    this.fieldOptionsGroup.get('numberDecimalPlaces').setValue(numberFormat.numberDecimalPlaces);
    this.fieldOptionsGroup.get('numberDecimalSeparator').setValue(numberFormat.numberDecimalSeparator);
    this.fieldOptionsGroup.get('numberThousandsSeparator').setValue(numberFormat.numberThousandsSeparator);

    this.documentOptionsGroup.get('outputFormat').setValue(config?.outputFormat ?? Adv2ConfigComponent.DEFAULT_OUTPUT_FORMAT);
    this.signatureGroup.get('errorNotificationEmail').setValue(config?.errorNotificationEmail ?? '');

    this.pendingSignature = { providerName: config?.signatureProviderName, signature };
    if (this.viewReady) {
      this.applyPendingSignature();
    }

    this.emptyFieldsFormArray.clear();
    if (config?.emptyFields) {
      emptyFieldsToControls(config.emptyFields).forEach((group) => this.emptyFieldsFormArray.push(group));
    }

    this.salesforceConfig = config?.salesforce ?? { objects: [] };
    this.salesforceMappingStore.primeFromConfig(this.salesforceConfig);

    this.formReady = true;
  }

  private applyPendingSignature(): void {
    if (!this.pendingSignature) {
      return;
    }

    // deliberately not guarded on signatureTab: an unresolved view query means seeding
    // silently did not happen, and the next save would wipe the corp's stored provider.
    // Failing loudly here matches how the pre-split code behaved on a missing provider.
    const { providerName, signature } = this.pendingSignature;
    this.pendingSignature = null;
    this.signatureTab.initialise(providerName, signature);
    this.updateValidationIcon();
  }

  private noSignatureProviderSelected(): boolean {
    return this.signatureGroup.get('signatureProvider').value === NoSignatureProviderComponent.NAME;
  }
}
